import type { Trip } from "../lib/api.ts";
import { Card } from "../ui/index.ts";

/**
 * The trip's capture window: starting it, and ending it. An admin's page, and the one thing on
 * Settings that changes what the trip is doing for everybody on it.
 *
 * It used to be a card sitting open on Settings, wearing the same border and padding as the trip
 * picker above it, so ending a trip's capture was one tap from arrival and looked like nothing in
 * particular. Here it has a page to itself and room to say what each switch does before it is
 * thrown.
 *
 * A trip that has ended keeps the page but offers no switch: capture does not restart, and a
 * button that cannot be pressed is worse than saying so.
 */
export function CapturePage({
  trip,
  lifecycle,
}: {
  trip: Trip;
  lifecycle: (action: "START" | "END") => Promise<void>;
}) {
  return (
    <div className="stack">
      <Card title={trip.state === "DRAFT" ? "Start the trip" : "Capture"}>
        <p className="muted small">
          {trip.state === "DRAFT"
            ? "Nothing is collected until the trip starts. From then on, footage taken during the trip is gathered from everyone who is sharing theirs."
            : trip.state === "ACTIVE"
              ? "Ending capture stops new footage being collected; what is already up stays, and the trip stays readable."
              : "Capture has ended. What was collected stays, and nothing new is gathered."}
        </p>
        {trip.state === "DRAFT" && (
          <div className="row wrap">
            <button className="button primary" onClick={() => void lifecycle("START")}>
              Start trip
            </button>
          </div>
        )}
        {trip.state === "ACTIVE" && (
          <div className="row wrap">
            <button className="button" onClick={() => void lifecycle("END")}>
              End capture
            </button>
          </div>
        )}
      </Card>
    </div>
  );
}
